import type { ComponentConfig } from '@wix/ambassador-ctp-gradual-rollout-v1-app-config/types';
import type { AppManifest } from '@wix/cli-app-manifest';
import type { HttpClient, HttpError } from '@wix/cli-http-client';
import type { Options } from 'p-retry';
import {
  commitComponentOverride,
  createAppRelease,
  createComponents,
  promoteAppVersionFromDraftToProduction,
  updateAppRelease,
} from '@wix/ambassador-ctp-gradual-rollout-v1-app-config/http';
import { release } from '@wix/ambassador-ctp-gradual-rollout-v1-baas-release/http';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { isHttpError, isHttpValidationError } from '@wix/cli-http-client';
import { getTestOverrides } from '@wix/cli-test-overrides';
import pRetry from 'p-retry';
import { fields, variant } from 'variant';
import { isTrustedInternalErr } from './isTrustedInternalErr.js';
import {
  commitComponentOverrideSchema,
  createAppReleaseSchema,
  promoteAppVersionFromDraftToProductionSchema,
  releaseSchema,
} from './schemas.js';

const { AppPermissionDenied } = CliErrorCode;

const UpdateAppReleaseData = variant({
  TagNotFound: fields<{ cause: HttpError }>(),
  TagUpdated: {},
});

export class ARMClient {
  private readonly retryOptions: Options;

  constructor(private readonly httpClient: HttpClient) {
    const { minRetryTimeout, retries } = getTestOverrides();

    this.retryOptions = {
      maxTimeout: 3 * 1000,
      minTimeout: minRetryTimeout,
      retries: retries ?? 3,
    };
  }

  createComponents = async (
    appId: string,
    componentConfigs: ComponentConfig[],
    projectId: string
  ) => {
    try {
      await this.httpClient.request(
        createComponents({ appId, componentConfigs, projectId })
      );
    } catch (e) {
      if (isHttpError(e) && e.response?.status === 403) {
        throw new CliError({
          cause: e,
          code: AppPermissionDenied({ appId }),
        });
      }
      throw new CliError({
        cause: e,
        code: CliErrorCode.FailedToCreateComponents(),
      });
    }
  };

  createAppRelease = async (manifest: AppManifest, projectId: string) => {
    try {
      const { data } = await pRetry(
        () =>
          this.httpClient.request(
            createAppRelease({
              appId: manifest.appId,
              componentConfigs: manifest.components,
              projectId,
            })
          ),
        this.retryOptions
      );

      return createAppReleaseSchema.parse(data).tag;
    } catch (e) {
      if (isHttpError(e)) {
        if (e.response?.status === 403) {
          throw new CliError({
            cause: e,
            code: AppPermissionDenied({ appId: manifest.appId }),
          });
        }

        if (e.response?.status === 400 && isHttpValidationError(e)) {
          throw new CliError({
            cause: e,
            code: CliErrorCode.AppReleaseConfigViolations(),
          });
        }
      }

      if (isTrustedInternalErr(e)) {
        throw new CliError({
          cause: e,
          code: CliErrorCode.TrustedAppIsNotMarkedAsInternal(),
        });
      }

      throw new CliError({
        cause: e,
        code: CliErrorCode.FailedToCreateAppRelease(),
      });
    }
  };

  updateAppRelease = async (manifest: AppManifest, tag: string) => {
    try {
      await pRetry(
        () =>
          this.httpClient.request(
            updateAppRelease({
              appId: manifest.appId,
              componentConfigs: manifest.components,
              tag,
            })
          ),
        this.retryOptions
      );

      return UpdateAppReleaseData.TagUpdated();
    } catch (e) {
      if (isHttpError(e)) {
        if (e.response?.status === 404) {
          return UpdateAppReleaseData.TagNotFound({ cause: e });
        }
        if (e.response?.status === 403) {
          throw new CliError({
            cause: e,
            code: AppPermissionDenied({ appId: manifest.appId }),
          });
        }
        if (e.response?.status === 400 && isHttpValidationError(e)) {
          throw new CliError({
            cause: e,
            code: CliErrorCode.AppReleaseConfigViolations(),
          });
        }
      }

      if (isTrustedInternalErr(e)) {
        throw new CliError({
          cause: e,
          code: CliErrorCode.TrustedAppIsNotMarkedAsInternal(),
        });
      }

      throw new CliError({
        cause: e,
        code: CliErrorCode.FailedToUpdateAppRelease(),
      });
    }
  };

  createDraftVersion = async (appId: string, tag: string) => {
    try {
      const { data } = await this.httpClient.request(
        commitComponentOverride({
          appId,
          componentOverrideId: tag,
        })
      );

      return commitComponentOverrideSchema.parse(data);
    } catch (cause) {
      throw new CliError({
        cause,
        code: CliErrorCode.FailedToCreateDraftVersion(),
      });
    }
  };

  releaseDraftVersion = async (
    appId: string,
    options: {
      createNewMajor: boolean;
      latestMajor: number;
      notes: string;
    }
  ) => {
    try {
      const { data } = await this.httpClient.request(
        promoteAppVersionFromDraftToProduction({
          appId,
          createNewMajor: options.createNewMajor,
          latestMajor: options.latestMajor,
          notes: options.notes,
        })
      );

      const { fullVersion } =
        promoteAppVersionFromDraftToProductionSchema.parse(data);

      return { releasedVersion: fullVersion };
    } catch (cause) {
      throw new CliError({
        cause,
        code: CliErrorCode.FailedToReleaseDraftVersion(),
      });
    }
  };

  releaseHeadlessProject = async ({
    appId,
    comment,
    componentOverrideId,
    createMinorVersion,
  }: {
    appId: string;
    comment?: string;
    componentOverrideId: string;
    createMinorVersion: boolean;
  }) => {
    try {
      const { data } = await this.httpClient.request(
        release({
          appId,
          componentOverrideId,
          createMinorVersion,
          notes: comment,
        })
      );

      return releaseSchema.parse(data).releaseBaseUrl;
    } catch (e) {
      throw new CliError({
        cause: e,
        code: CliErrorCode.FailedToReleaseHeadlessProject(),
      });
    }
  };
}
